import { Location } from "react-router-dom";

import { OrganisationTypeConstants } from "../constants";
import {
  isIssuanceTransactionObjectType,
  ISSUANCE_TRANSACTION_DEEP_LINK_PARAM,
} from "../constants/project-issuance-transaction/ProjectIssuanceTransactionConstants";
import { isTaskObjectType } from "../constants/workflow/TaskConstants";
import { NotificationTarget } from "../models/notification";
import { NewDealJourneyParams } from "../models/routeParams/NewDealJourneyParams";
import { logError } from "../service/error";

export const getLogonRoute = (params?: { origin: string }): string => {
  return `/logon${params?.origin ? `?origin=${params.origin}` : ""}`;
};

export const getForgottenPasswordRoute = (): string => {
  return "/forgotten-password";
};

export const getDashboardRoute = (userType: OrganisationTypeConstants): string => {
  const sharedUrl = "/dashboard";

  switch (userType) {
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    case OrganisationTypeConstants.VERIFIER:
      return `/v${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    default:
      logError({
        error: `The following organisation type does not exist:  ${userType}`,
      });
      return "";
  }
};

export const getVerifierQueueManagementAssessmentsRoute = (userType: OrganisationTypeConstants): string => {
  const sharedUrl = "/queue-management/assessments";

  switch (userType) {
    case OrganisationTypeConstants.VERIFIER:
      return `/v${sharedUrl}`;
    default:
      logError({
        error: `The following organisation type does not exist:  ${userType}`,
      });
      return "";
  }
};

export const getVerifierQueueManagementReviewsRoute = (userType: OrganisationTypeConstants): string => {
  const sharedUrl = "/queue-management/reviews";

  switch (userType) {
    case OrganisationTypeConstants.VERIFIER:
      return `/v${sharedUrl}`;
    default:
      logError({
        error: `The following organisation type does not exist:  ${userType}`,
      });
      return "";
  }
};

export const getVerifierQueueManagementUnassignedActivitiesRoute = (userType: OrganisationTypeConstants): string => {
  const sharedUrl = "/queue-management/unassigned-activities";

  switch (userType) {
    case OrganisationTypeConstants.VERIFIER:
      return `/v${sharedUrl}`;
    default:
      logError({
        error: `The following organisation type does not exist:  ${userType}`,
      });
      return "";
  }
};

export const getUserSettingsRoute = (userType: OrganisationTypeConstants): string => {
  const sharedUrl = "/settings/users";

  switch (userType) {
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    case OrganisationTypeConstants.VERIFIER:
      return `/v${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    default:
      logError({
        error: `The following organisation type does not exist:  ${userType}`,
      });
      return "";
  }
};

export const getUserSettingsOrganisationRoute = (
  userType: OrganisationTypeConstants,
  params?: {
    origin: string;
  }
): string => {
  const sharedUrl = `/settings/organisation${params?.origin ? `?origin=${params.origin}` : ""}`;

  switch (userType) {
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    case OrganisationTypeConstants.VERIFIER:
      return `/v${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    default:
      logError({
        error: `The following organisation type does not exist:  ${userType}`,
      });
      return "";
  }
};

export const getDeveloperSettingsMarketingAssetsRoute = (
  params?: {
    origin: string;
  },
  uuid: string | undefined = undefined
): string => {
  return `/d/settings/marketing-assets${uuid ? `/${uuid}` : ""}${params?.origin ? `?origin=${params.origin}` : ""}`;
};

export const getVerifierSettingsMarketingAssetsRoute = (
  params?: {
    origin: string;
  },
  uuid: string | undefined = undefined
): string => {
  return `/v/settings/marketing-assets${uuid ? `/${uuid}` : ""}${params?.origin ? `?origin=${params.origin}` : ""}`;
};

export const getDevelopmentManagerSettingsMarketingAssetsRoute = (
  params?: {
    origin: string;
  },
  uuid: string | undefined = undefined
): string => {
  return `/dm/settings/marketing-assets${uuid ? `/${uuid}` : ""}${params?.origin ? `?origin=${params.origin}` : ""}`;
};

export const getProjectsRoute = (userType: OrganisationTypeConstants): string => {
  const sharedUrl = "/projects";

  switch (userType) {
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    case OrganisationTypeConstants.VERIFIER:
      return `/v${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    default:
      logError({
        error: `The following organisation type does not exist:  ${userType}`,
      });
      return "";
  }
};

export const getProjectTrackerRoute = (userType: OrganisationTypeConstants): string => {
  const sharedUrl = "/projects/project-tracker";

  switch (userType) {
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    default:
      logError({
        error: `The following organisation type does not exist:  ${userType}`,
      });
      return "";
  }
};

export const getNewProjectRoute = (userType: OrganisationTypeConstants): string => {
  const sharedUrl = "/projects/new-project";

  switch (userType) {
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    default:
      logError({
        error: `In 'routes.ts, method getNewProjectRoute, the following organisation type does not exists:  ${userType}`,
      });
      return "";
  }
};

export const getProjectRoadmapRoute = (userType: OrganisationTypeConstants): string => {
  const sharedUrl = `/projects/project-roadmap`;

  switch (userType) {
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    default:
      logError({
        error: `The following organisation type does not exist:  ${userType}`,
      });
      return "";
  }
};

export const getActivitiesRoute = (userType: OrganisationTypeConstants): string => {
  const sharedUrl = "/activities";

  switch (userType) {
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    case OrganisationTypeConstants.VERIFIER:
      return `/v${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    default:
      logError({
        error: `The following organisation type does not exist:  ${userType}`,
      });
      return "";
  }
};

export const getSettingsRoute = (userType: OrganisationTypeConstants): string => {
  const sharedUrl = "/settings";

  switch (userType) {
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    case OrganisationTypeConstants.VERIFIER:
      return `/v${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    default:
      logError({
        error: `The following organisation type does not exist:  ${userType}`,
      });
      return "";
  }
};

export const getMarketingRoute = (): string => {
  return "/d/marketing/marketing-stats";
};

export const getProjectDetailsByUuid = (
  uuid: string,
  userType: OrganisationTypeConstants,
  params?: {
    origin: string;
  }
): string => {
  const sharedUrl = `/projects/${uuid}/overview${params?.origin ? `?origin=${params.origin}` : ""}`;
  switch (userType) {
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    case OrganisationTypeConstants.VERIFIER:
      return `/v${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    default:
      logError({
        error: `In 'routes.ts, method getProjectDetailsByUuid', the following organisation type does not exists:  ${userType}`,
      });
      return "";
  }
};

export const getProjectDetailsRoute = (
  uuid: string,
  userType: OrganisationTypeConstants,
  params?: {
    origin: string;
  }
): string => {
  const sharedUrl = `/projects/${uuid}/details${params?.origin ? `?origin=${params.origin}` : ""}`;

  switch (userType) {
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    case OrganisationTypeConstants.VERIFIER:
      return `/v${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    default:
      logError({
        error: `In 'routes.ts, method getProjectDetailsRoute', the following organisation type does not exists:  ${userType}`,
      });
      return "";
  }
};

export const getProjectMarketingSettingsRoute = (
  uuid: string,
  userType: OrganisationTypeConstants,
  queryParams?: string
): string => {
  const sharedUrl = `/projects/${uuid}/marketing-settings${queryParams ? `?${queryParams}` : ""}`;

  // TODO: Implement other user types when we introduce audit for them
  switch (userType) {
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    case OrganisationTypeConstants.VERIFIER: // This shouldn't be called but will prevent error
      return "";
    default:
      logError({
        error: `In 'routes.ts, method getProjectMarketingSettingsRoute', the following organisation type does not exists:  ${userType}`,
      });
      return "";
  }
};

export const getProjectAccessRoute = (
  uuid: string,
  userType: OrganisationTypeConstants,
  params?: {
    origin: string;
  }
): string => {
  const sharedUrl = `/projects/${uuid}/access${params?.origin ? `?origin=${params.origin}` : ""}`;

  switch (userType) {
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    default:
      logError({
        error: `In 'routes.ts, method getProjectAccessRoute', the following organisation type does not exists:  ${userType}`,
      });
      return "";
  }
};

export const getProjectAuditHistoryRoute = (uuid: string, userType: OrganisationTypeConstants): string => {
  const sharedUrl = `/projects/${uuid}/audit-history`;

  switch (userType) {
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    case OrganisationTypeConstants.VERIFIER:
      return `/v${sharedUrl}`;
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    default:
      logError({
        error: `In 'routes.ts, method getProjectAuditHistoryRoute', the following organisation type does not exists:  ${userType}`,
      });
      return "";
  }
};

export const getProjectDocumentsRoute = (
  uuid: string,
  userType: OrganisationTypeConstants,
  params?: {
    origin: string;
  }
): string => {
  const sharedUrl = `/projects/${uuid}/documents${params?.origin ? `?origin=${params.origin}` : ""}`;

  switch (userType) {
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    case OrganisationTypeConstants.VERIFIER:
      return `/v${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    default:
      logError({
        error: `In 'routes.ts, method getProjectDocumentsRoute', the following organisation type does not exists:  ${userType}`,
      });
      return "";
  }
};

export const getProjectLocationRoute = (
  uuid: string,
  userType: OrganisationTypeConstants,
  params?: {
    origin: string;
  }
): string => {
  const sharedUrl = `/projects/${uuid}/location${params?.origin ? `?origin=${params.origin}` : ""}`;

  switch (userType) {
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    case OrganisationTypeConstants.VERIFIER:
      return `/v${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    default:
      logError({
        error: `In 'routes.ts, method getProjectLocationRoute', the following organisation type does not exists:  ${userType}`,
      });
      return "";
  }
};

export const getProjectActivityReviewRoute = (projectUuid: string, userType: OrganisationTypeConstants): string => {
  const sharedUrl = `/projects/${projectUuid}/activity-review`;

  switch (userType) {
    case OrganisationTypeConstants.VERIFIER:
      return `/v${sharedUrl}`;
    default:
      logError({
        error: `In 'routes.ts, method getProjectActivityReviewRoute', the following organisation type does not exists:  ${userType}`,
      });
      return "";
  }
};

export const getOperationsDealWorkflowsRoute = (userType: OrganisationTypeConstants): string => {
  const sharedUrl = "/operations/workflows/deal";

  switch (userType) {
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    default:
      logError({
        error: `In 'routes.ts, method getOperationsWorkflowsRoute, the following organisation type does not exists: ${userType}`,
      });
      return "";
  }
};

export const getOperationsDeliveryWorkflowsRoute = (userType: OrganisationTypeConstants): string => {
  const sharedUrl = "/operations/workflows/delivery";

  switch (userType) {
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    default:
      logError({
        error: `In 'routes.ts, method getOperationsWorkflowsRoute, the following organisation type does not exists: ${userType}`,
      });
      return "";
  }
};

export const getWorkflowTemplatesRoute = (
  userType: OrganisationTypeConstants,
  templateUuid: string | undefined = undefined
): string => {
  const sharedUrl = `/workflows/templates`;

  switch (userType) {
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}/${templateUuid ?? ""}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}/${templateUuid ?? ""}`;
    case OrganisationTypeConstants.ASSET_MANAGER:
      return getOperationsDeliveryWorkflowsRoute(OrganisationTypeConstants.ASSET_MANAGER);
    default:
      logError({
        error: `In 'routes.ts, method getWorkflowTemplatesRoute', the following organisation type does not exists:  ${userType}`,
      });
      return "";
  }
};

export const getWorkflowTasksRoute = (userType: OrganisationTypeConstants): string => {
  const sharedUrl = `/workflows/tasks`;

  switch (userType) {
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    // Seed groups this under Operations rather than Workflows, matching its navigation.
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am/operations/tasks`;
    default:
      logError({
        error: `In 'routes.ts, method getWorkflowTasksRoute', the following organisation type does not exists:  ${userType}`,
      });
      return "";
  }
};

export const getWorkflowTemplateRoute = (uuid: string, userType: OrganisationTypeConstants): string => {
  const sharedUrl = `/workflows/templates/${uuid}`;

  switch (userType) {
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    default:
      logError({
        error: `In 'routes.ts, method getWorkflowTemplateRoute', the following organisation type does not exists:  ${userType}`,
      });
      return "";
  }
};

export const getWorkflowTasksBoardRoute = (
  workflowUuid: string,
  userType: OrganisationTypeConstants,
  params?: {
    origin: string;
  }
): string => {
  switch (userType) {
    case OrganisationTypeConstants.DEVELOPER:
      return `/d/workflows/tasks-board/${workflowUuid}${params?.origin ? `?origin=${params.origin}` : ""}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm/workflows/tasks-board/${workflowUuid}${params?.origin ? `?origin=${params.origin}` : ""}`;
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am/workflows/tasks-board/${workflowUuid}${params?.origin ? `?origin=${params.origin}` : ""}`;
    default:
      logError({
        error: `In 'routes.ts, method getProjectTasksBoardRoute', the following organisation type does not exists:  ${userType}`,
      });
      return "";
  }
};

export const getInsightsRoute = (userType: OrganisationTypeConstants): string => {
  const sharedUrl = `/insights`;

  switch (userType) {
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    default:
      logError({
        error: `In 'routes.ts, method getProjectInsightsRoute', the following organisation type does not exists:  ${userType}`,
      });
      return "";
  }
};

export const getWatchlistRoute = (userType: OrganisationTypeConstants): string => {
  const sharedUrl = `/watchlist`;

  switch (userType) {
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    default:
      logError({
        error: `In 'routes.ts, method getProjectWatchlistRoute', the following organisation type does not exists:  ${userType}`,
      });
      return "";
  }
};

export const getProjectManageProjectGroupRoute = (uuid: string, queryParams: string): string => {
  return `/d/projects/${uuid}/manage-project-group${queryParams ? `?${queryParams}` : ""}`;
};

export const getManageLandholdingRoute = (
  uuid: string,
  userType: OrganisationTypeConstants,
  queryParams: string
): string => {
  const sharedUrl = `/landholding/${uuid}/manage-landholding${queryParams ? `?${queryParams}` : ""}`;

  switch (userType) {
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    default:
      logError({
        error: `In 'routes.ts, method getManageLandholdingRoute, the following organisation type does not exists:  ${userType}`,
      });
      return "";
  }
};

// projectUuid is required: it is a path segment, so an omitted one used to put the literal string
// "undefined" in the URL rather than failing.
export const getProjectAccessDetailsRoute = (
  uuid: string,
  userType: OrganisationTypeConstants,
  projectUuid: string,
  params?: { origin: string }
): string => {
  const sharedUrl = `/projects/${projectUuid}/access/invite/${uuid}${params?.origin ? `?origin=${params.origin}` : ""}`;

  switch (userType) {
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    default:
      logError({
        error: `In 'routes.ts, method getProjectAccessDetailsRoute', the following organisation type does not exists:  ${userType}`,
      });
      return "";
  }
};

export const getProjectAccessInviteAcceptRoute = (uuid: string, userType: OrganisationTypeConstants): string => {
  const sharedUrl = `/project/access/invite/accept/${uuid}`;

  switch (userType) {
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    default:
      logError({
        error: `In 'routes.ts, method getProjectAccessInviteAcceptRoute', the following organisation type does not exists:  ${userType}`,
      });
      return "";
  }
};

export const getUserEditRouteByUuid = (
  uuid: string,
  userType: OrganisationTypeConstants,
  params?: {
    origin: string;
  }
): string => {
  const sharedUrl = `/settings/users/${uuid}/edit${params?.origin ? `?origin=${params.origin}` : ""}`;

  switch (userType) {
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    case OrganisationTypeConstants.VERIFIER:
      return `/v${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    default:
      logError({
        error: `The following organisation type does not exist:  ${userType}`,
      });
      return "";
  }
};

export const getUserSettingsSendInvitationRoute = (userType: OrganisationTypeConstants): string => {
  const sharedUrl = "/settings/users/send-invitation";

  switch (userType) {
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    case OrganisationTypeConstants.VERIFIER:
      return `/v${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    default:
      logError({
        error: `The following organisation type does not exist:  ${userType}`,
      });
      return "";
  }
};

export const getSettingsProjectAccessRoute = (userType: OrganisationTypeConstants): string => {
  const sharedUrl = "/settings/project-access";

  switch (userType) {
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    default:
      logError({
        error: `In 'routes.ts, method getSettingsProjectAccessRoute', the following organisation type does not exists:  ${userType}`,
      });
      return "";
  }
};

export const getSettingsDocumentTypesRoute = (userType: OrganisationTypeConstants): string => {
  const sharedUrl = "/settings/document-types";

  switch (userType) {
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    default:
      logError({
        error: `In 'routes.ts, method getSettingsDocumentTypesRoute', the following organisation type does not exists:  ${userType}`,
      });
      return "";
  }
};

export const getSettingsAuditHistoryRoute = (userType: OrganisationTypeConstants): string => {
  const sharedUrl = "/settings/audit-history";

  switch (userType) {
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    default:
      logError({
        error: `In 'routes.ts, method getSettingsAuditHistoryRoute', the following organisation type does not exists:  ${userType}`,
      });
      return "";
  }
};

export const getSettingsViewDocumentTypeRoute = (
  documentTypeUuid: string,
  userType: OrganisationTypeConstants
): string => {
  const sharedUrl = `/settings/document-types/${documentTypeUuid}/view`;

  switch (userType) {
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    default:
      logError({
        error: `In 'routes.ts, method getSettingsViewDocumentTypeRoute', the following organisation type does not exists:  ${userType}`,
      });
      return "";
  }
};

export const getProfileChangePasswordRoute = (userType: OrganisationTypeConstants): string => {
  const sharedUrl = "/profile/change-password";

  switch (userType) {
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    case OrganisationTypeConstants.VERIFIER:
      return `/v${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    default:
      logError({
        error: `In 'routes.ts, method getProfileChangePasswordRoute', the following organisation type does not exist:  ${userType}`,
      });
      return "";
  }
};

export const getProfileChangePersonalDetailsRoute = (userType: OrganisationTypeConstants): string => {
  const sharedUrl = "/profile/change-personal-details";

  switch (userType) {
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    case OrganisationTypeConstants.VERIFIER:
      return `/v${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    default:
      logError({
        error: `In 'routes.ts, method getProfileChangePersonalDetailsRoute', the following organisation type does not exist:  ${userType}`,
      });
      return "";
  }
};

export const getPublicProjectsRoute = (): string => "/p/projects";

export const getPublicCodeDetailsRoute = (uuid: string): string => `/p/code/${uuid}/details`;

export const getPublicBadgeDetailsRoute = (uuid: string): string => `/p/badge/${uuid}/details`;

export const getPublicDeveloperDetailsRoute = (uuid: string): string => `/p/developer/${uuid}/details`;

export const getPublicDevelopmentManagerDetailsRoute = (uuid: string): string =>
  `/p/development-manager/${uuid}/details`;

export const getPublicDeveloperDetailsPreviewRoute = (uuid: string): string => `/d/developer/details/preview/${uuid}`;
export const getPublicVerifierDetailsPreviewRoute = (uuid: string): string => `/v/verifier/details/preview/${uuid}`;
export const getPublicDevelopmentManagerDetailsPreviewRoute = (uuid: string): string =>
  `/dm/development-manager/details/preview/${uuid}`;
export const getCodePublicProjectsRoute = (codeUuid?: string, notCodeUuids?: string[]): string => {
  const falseCodeUrls = notCodeUuids?.map((uuid) => `%2C${uuid}%3Afalse`);
  return codeUuid && notCodeUuids ? `/p/projects?code=${codeUuid}%3Atrue${falseCodeUrls}` : "/p/projects";
};
export const getBadgePublicProjectsRoute = (uuid?: string): string =>
  uuid ? `/p/projects?badges=${uuid}` : "/p/projects";

export const getPublicProjectOverviewRoute = (uuid: string): string => {
  return `/p/project/${uuid}/overview`;
};

export const getPublicProjectDetailsRoute = (uuid: string): string => {
  return `/p/project/${uuid}/details`;
};

export const getPublicProjectDocumentsRoute = (uuid: string): string => {
  return `/p/project/${uuid}/documents`;
};

export const getPublicProjectInventoryRoute = (uuid: string): string => {
  return `/p/project/${uuid}/inventory`;
};

export const getPublicProjectOverviewPreviewRoute = (
  uuid: string,
  previewUuid: string,
  userType: OrganisationTypeConstants
): string => {
  const sharedUrl = `/project/${uuid}/preview/${previewUuid}/overview`;

  switch (userType) {
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    default:
      logError({
        error: `In 'routes.ts, method getPublicProjectOverviewPreviewRoute', the following organisation type does not exists:  ${userType}`,
      });
      return "";
  }
};

export const getPublicValidatorRoute = (uuid: string): string => {
  return `/p/verifier/${uuid}/details`;
};

export const getPublicProjectTypeRoute = (code: string, queryParams?: string): string => {
  return `/p/project-type/${code}${queryParams ? `?${queryParams}` : ""}`;
};

export const getPublicCodeComparisonRoute = (): string => {
  return "/p/code-comparison";
};

export const getPublicProjectTypesAndStandardsRoute = (): string => {
  return "/p/project-types-and-standards";
};

export const getPublicProjectTypesRoute = (): string => {
  return `/p/project-types`;
};

export const getPublicPageNotFoundRoute = (): string => {
  return `/p/404/page-not-found`;
};

export const getAccessDeniedRoute = (userType?: OrganisationTypeConstants): string => {
  const sharedUrl = "/403/access-denied";

  switch (userType) {
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    case OrganisationTypeConstants.VERIFIER:
      return `/v${sharedUrl}`;
    default:
      return `/d${sharedUrl}`;
  }
};

export const getActivityViewRoute = (
  activityHistoryUuid: string,
  userType: OrganisationTypeConstants,
  queryParams?: string
): string => {
  const sharedUrl = `/activities/${activityHistoryUuid}/view${queryParams ? `?${queryParams}` : ""}`;

  switch (userType) {
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    case OrganisationTypeConstants.VERIFIER:
      return `/v${sharedUrl}`;
    default:
      logError({
        error: `In 'routes.ts, method getActivityViewRoute', the following organisation type does not exist:  ${userType}`,
      });
      return "";
  }
};

export const getActivityDashboardTabRoute = (
  activityUuid: string,
  tab: string,
  userType: OrganisationTypeConstants,
  queryParams?: string
): string => {
  const sharedUrl = `/activity/${activityUuid}/${tab}${queryParams ? `?${queryParams}` : ""}`;

  switch (userType) {
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    case OrganisationTypeConstants.VERIFIER: // This shouldn't be called but will prevent error
      return "";
    default:
      logError({
        error: `In 'routes.ts, method getActivityDashboardTabRoute', the following organisation type does not exist:  ${userType}`,
      });
      return "";
  }
};

export const getActivityEditRoute = (
  activityReviewUuid: string,
  userType: OrganisationTypeConstants,
  queryParams?: string
): string => {
  const sharedUrl = `/activities/${activityReviewUuid}/edit${queryParams ? `?${queryParams}` : ""}`;

  switch (userType) {
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    case OrganisationTypeConstants.VERIFIER: // This shouldn't be called but will prevent error
      return "";
    default:
      logError({
        error: `In 'routes.ts, method getActivityEditRoute', the following organisation type does not exist:  ${userType}`,
      });
      return "";
  }
};

export const getActivityReviewDashboardTabRoute = (
  activityReviewActivityUuid: string,
  tab: string,
  queryParams?: string
): string => {
  return `/v/activity-review/${activityReviewActivityUuid}/${tab}${queryParams ? `?${queryParams}` : ""}`;
};

export const getActivityReviewRoute = (activityHistoryUuid: string, queryParams?: string): string => {
  return `/v/activity-review/${activityHistoryUuid}/review${queryParams ? `?${queryParams}` : ""}`;
};

export const getActivityReviewViewRoute = (activityHistoryUuid: string, queryParams?: string): string => {
  return `/v/activity-review/${activityHistoryUuid}/view${queryParams ? `?${queryParams}` : ""}`;
};

export const getActivityDocumentRoute = (
  activityUuid: string,
  activityDocumentUuid: string,
  userType: OrganisationTypeConstants,
  queryParams?: string
): string => {
  const sharedUrl = `/activity/${activityUuid}/document/${activityDocumentUuid}${queryParams ? `?${queryParams}` : ""}`;

  switch (userType) {
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    case OrganisationTypeConstants.VERIFIER: // This shouldn't be called but will prevent error
      return `/v${sharedUrl}`;
    default:
      logError({
        error: `In 'routes.ts, method getActivityDocumentRoute', the following organisation type does not exists:  ${userType}`,
      });
      return "";
  }
};

export const getActivityDocumentHistoryDownload = (activityHistoryUuid: string, mimeType: string): string => {
  return `/activity/file/download?ActivityDocumentHistoryUuid=${activityHistoryUuid}&MimeType=${encodeURIComponent(
    mimeType
  )}`;
};

export const getProjectActivitiesRoute = (
  projectUuid: string,
  userType: OrganisationTypeConstants,
  params?: {
    origin: string;
  }
): string => {
  const sharedUrl = `/projects/${projectUuid}/activities${params?.origin ? `?origin=${params.origin}` : ""}`;

  switch (userType) {
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    case OrganisationTypeConstants.VERIFIER: // This shouldn't be called but will prevent error
      return "";
    default:
      logError({
        error: `In 'routes.ts, method getProjectActivitiesRoute', the following organisation type does not exists:  ${userType}`,
      });
      return "";
  }
};

export const getProjectInventoryRoute = (
  projectUuid: string,
  userType: OrganisationTypeConstants,
  params?: {
    origin: string;
  }
): string => {
  const sharedUrl = `/projects/${projectUuid}/inventory${params?.origin ? `?origin=${params.origin}` : ""}`;

  switch (userType) {
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    case OrganisationTypeConstants.VERIFIER:
      return `/v${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    default:
      logError({
        error: `In 'routes.ts, method getProjectInventoryRoute', the following organisation type does not exists:  ${userType}`,
      });
      return "";
  }
};

export const getProjectTasksRoute = (
  projectUuid: string,
  userType: OrganisationTypeConstants,
  params?: {
    origin: string;
  }
): string => {
  const sharedUrl = `/projects/${projectUuid}/tasks${params?.origin ? `?origin=${params.origin}` : ""}`;

  switch (userType) {
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    default:
      logError({
        error: `In 'routes.ts, method getProjectTasksRoute', the following organisation type does not exists:  ${userType}`,
      });
      return "";
  }
};

export const getDocumentHistoryRoute = (
  projectUuid: string,
  projectDocumentUuid: string,
  userType: OrganisationTypeConstants,
  params?: { origin: string },
  groupUuid?: string
): string => {
  const sharedUrl = `/projects/${projectUuid}/documents/${projectDocumentUuid}${
    params?.origin ? `?origin=${params.origin}` : ""
  }${groupUuid ? `${params?.origin ? "&" : "?"}groupuuid=${groupUuid}` : ""}`;

  switch (userType) {
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    case OrganisationTypeConstants.VERIFIER:
      return `/v${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    default:
      logError({
        error: `In 'routes.ts, method getDocumentHistoryRoute', the following organisation type does not exists:  ${userType}`,
      });
      return "";
  }
};

export const getPathnameWithoutLastElement = (pathName: string): string => {
  return pathName.substring(0, pathName.lastIndexOf("/"));
};

export const getProjectPathnameForBackNavigation = (
  projectUuid: string,
  previousLocation: Location | undefined
): string => {
  const routePathname = getPublicProjectOverviewRoute(projectUuid);

  if (
    previousLocation &&
    getPathnameWithoutLastElement(routePathname) === getPathnameWithoutLastElement(previousLocation.pathname)
  ) {
    return routePathname + previousLocation.search;
  }

  return routePathname;
};

export const getInventoryRoute = (userType: OrganisationTypeConstants): string => {
  const sharedUrl = "/inventory";

  switch (userType) {
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    case OrganisationTypeConstants.VERIFIER:
      return `/v${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    default:
      logError({
        error: `The following organisation type does not exist:  ${userType}`,
      });
      return "";
  }
};

export const getLandholdingDashboardRoute = (uuid: string, userType: OrganisationTypeConstants): string => {
  const sharedUrl = `/landholding/${uuid}/details`;
  switch (userType) {
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    default:
      logError({
        error: `In 'routes.ts, method getLandholdingDashboardRoute, the following organisation type does not exists:  ${userType}`,
      });
      return "";
  }
};

export const getLandholdingLocationRoute = (uuid: string, userType: OrganisationTypeConstants): string => {
  const sharedUrl = `/landholding/${uuid}/location`;
  switch (userType) {
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    default:
      logError({
        error: `In 'routes.ts, method getLandholdingLocationRoute, the following organisation type does not exists:  ${userType}`,
      });
      return "";
  }
};

export const getLandholdingInventoryRoute = (uuid: string, userType: OrganisationTypeConstants): string => {
  const sharedUrl = `/landholding/${uuid}/inventory`;
  switch (userType) {
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    default:
      logError({
        error: `In 'routes.ts, method getLandholdingInventoryRoute, the following organisation type does not exists:  ${userType}`,
      });
      return "";
  }
};

export const getLandholdingDocumentsRoute = (uuid: string, userType: OrganisationTypeConstants): string => {
  const sharedUrl = `/landholding/${uuid}/documents`;
  switch (userType) {
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    default:
      logError({
        error: `In 'routes.ts, method getLandholdingDocumentsRoute, the following organisation type does not exists:  ${userType}`,
      });
      return "";
  }
};

export const getLandholdingTasksRoute = (uuid: string, userType: OrganisationTypeConstants): string => {
  const sharedUrl = `/landholding/${uuid}/tasks`;
  switch (userType) {
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    default:
      logError({
        error: `In 'routes.ts, method getLandholdingTasksRoute, the following organisation type does not exists:  ${userType}`,
      });
      return "";
  }
};

// `tab` optionally deep-links to a specific tab via the ?tab= query param (see dealTabs.ts for slugs).
export const getDealListingRoute = (uuid: string, userType: OrganisationTypeConstants, tab?: string): string => {
  const sharedUrl = `/deal/${uuid}/listing${tab ? `?tab=${tab}` : ""}`;
  switch (userType) {
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    default:
      logError({
        error: `In 'routes.ts, method getDealListingRoute, the following organisation type does not exists: ${userType}`,
      });
      return "";
  }
};

export const getDealDocumentDetailsRoute = (
  dealUuid: string,
  dealDocumentUuid: string,
  userType: OrganisationTypeConstants
): string => {
  const sharedUrl = `/deal/${dealUuid}/documents/${dealDocumentUuid}`;
  switch (userType) {
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    default:
      logError({
        error: `In 'routes.ts, method getDealDocumentDetailsRoute, the following organisation type does not exists: ${
          userType
        }`,
      });
      return "";
  }
};

export const getPortfolioRoute = (userType: OrganisationTypeConstants): string => {
  const sharedUrl = "/portfolio";

  switch (userType) {
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    default:
      logError({
        error: `In 'routes.ts, method getPortfolioRoute, the following organisation type does not exists: ${userType}`,
      });
      return "";
  }
};

export const getPipelineTrackerRoute = (userType: OrganisationTypeConstants): string => {
  const sharedUrl = "/portfolio/pipeline-tracker";

  switch (userType) {
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    default:
      logError({
        error: `In 'routes.ts, method getPipelineTrackerRoute, the following organisation type does not exists: ${userType}`,
      });
      return "";
  }
};

export const getPipelineRoadmapRoute = (userType: OrganisationTypeConstants): string => {
  const sharedUrl = `/portfolio/pipeline-roadmap`;

  switch (userType) {
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    default:
      logError({
        error: `In 'routes.ts, method getPipelineRoadmapRoute, the following organisation type does not exists: ${userType}`,
      });
      return "";
  }
};

export const getInvestmentDirectoryRoute = (userType: OrganisationTypeConstants): string => {
  const sharedUrl = "/investment-directory";

  switch (userType) {
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    default:
      logError({
        error: `In 'routes.ts, method getInvestmentDirectoryRoute, the following organisation type does not exists: ${userType}`,
      });
      return "";
  }
};

export const getNewDealRoute = (params?: string): string => {
  return `/am/new-deal${params ? `?${params}` : ""}`;
};

// Opens the new deal journey with the given object already associated with the deal.
export const getNewDealForObjectRoute = (objectUuid: string, objectType: string, objectName?: string): string => {
  const params = new URLSearchParams();

  params.set(NewDealJourneyParams.objectUuid, objectUuid);
  params.set(NewDealJourneyParams.objectType, objectType);

  if (objectName) {
    params.set(NewDealJourneyParams.objectName, objectName);
  }

  return getNewDealRoute(params.toString());
};

export const getOperationsCompaniesRoute = (userType: OrganisationTypeConstants): string => {
  const sharedUrl = "/operations/companies";

  switch (userType) {
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    default:
      logError({
        error: `In 'routes.ts, method getOperationsCompaniesRoute, the following organisation type does not exists: ${userType}`,
      });
      return "";
  }
};

export const getCollaboratorRoute = (collaboratorUuid: string, userType: OrganisationTypeConstants): string => {
  const companiesRoute = getOperationsCompaniesRoute(userType);
  return `${companiesRoute}?companyUuid=${collaboratorUuid}`;
};

const withParam = (url: string, key: string, value: string): string =>
  `${url}${url.includes("?") ? "&" : "?"}${key}=${value}`;

/*
  A notification's target route: its own relative url under the role prefix, plus a query param naming the
  object it is about when the url alone cannot reach it. That uuid comes from the notification's objectUuid
  rather than out of the url — a workflow task's link names only its board, and a quote-expiry reminder's
  only its project, so neither url can say which task or which quote to open. The destination page reads the
  param: a task grid opens that task's overlay, the project Inventory tab opens that quote's issuance in the
  Unit transactions panel. Deciding it here rather than at the call site keeps every caller from having to
  know which object types carry an openable uuid.
*/
export const getNotificationRoute = (notification: NotificationTarget, userType: OrganisationTypeConstants): string => {
  const { url, objectType, objectUuid } = notification;

  let sharedUrl = url;
  if (isTaskObjectType(objectType)) {
    sharedUrl = withParam(url, "taskUuid", objectUuid);
  } else if (objectUuid && isIssuanceTransactionObjectType(objectType)) {
    sharedUrl = withParam(url, ISSUANCE_TRANSACTION_DEEP_LINK_PARAM, objectUuid);
  }

  switch (userType) {
    case OrganisationTypeConstants.ASSET_MANAGER:
      return `/am${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPMENT_MANAGER:
      return `/dm${sharedUrl}`;
    case OrganisationTypeConstants.DEVELOPER:
      return `/d${sharedUrl}`;
    case OrganisationTypeConstants.VERIFIER:
      return `/v${sharedUrl}`;
    default:
      logError({
        error: `In 'routes.ts, method getNotificationRoute, the following organisation type does not exists:  ${userType}`,
      });
      return "";
  }
};

/**
 * The route for a workflow task's parent object, from the uniform `parentObject*` fields — which
 * span Project, Deal and Landholding and are set for general tasks too.
 *
 * Returns `undefined` rather than a path when the object cannot be reached: an unknown parent type,
 * a missing uuid, or a parent whose route has no variant for this role (a Deal has no Developer or
 * Verifier route, a Landholding no Verifier route). Callers use that to render the object name as
 * plain text instead of as a link that would dead-end.
 *
 * No `origin` query param: only the project pages read one, and the Landholding destination's
 * back-button switch has no case for the pages linking here — it would fall through to a hardcoded
 * Developer project route built from a uuid it does not have. Back-navigation context travels as
 * router state instead; see `getTaskParentObjectNavigation`.
 */
export const getTaskParentObjectRoute = (
  parentObjectType: string | null | undefined,
  parentObjectUuid: string | null | undefined,
  userType: OrganisationTypeConstants
): string | undefined => {
  if (!parentObjectUuid || !parentObjectType) {
    return undefined;
  }

  let route: string;
  switch (parentObjectType.toLowerCase()) {
    case "project":
      route = getProjectDetailsByUuid(parentObjectUuid, userType);
      break;
    case "deal":
      route = getDealListingRoute(parentObjectUuid, userType);
      break;
    case "landholding":
      route = getManageLandholdingRoute(parentObjectUuid, userType, "");
      break;
    default:
      return undefined;
  }

  // The route helpers return "" for a role they have no variant for, having already logged it.
  return route || undefined;
};
